import { useMutation, useQueryClient } from 'react-query';
import { useDispatch } from 'react-redux';

import {
  toastrError,
  toastrSuccess,
  toastrWarning,
} from '@portals/redux/actions/toastr';
import {
  DeviceType,
  RESULTS_PER_PAGE,
  TableColumn,
  TableState,
} from '@portals/types';

import { DEVICES_API_URL, devicesQueryKeys } from './devices.constants';
import { DeviceStateHistory } from './devices.types';
import { useApiQuery } from '../../hooks';
import {
  fetchApiRequest,
  usePaginatedTableApiQuery,
  useRequestOptions,
} from '../../utils';

export function usePaginatedDevicesData({ pageIndex }: { pageIndex: number }) {
  return usePaginatedTableApiQuery<DeviceType>({
    baseUrl: DEVICES_API_URL,
    queryKey: devicesQueryKeys.list,
    tableState: {
      pageIndex,
      pageSize: RESULTS_PER_PAGE,
    },
  });
}

export function usePaginatedDevicesTableData(
  tableState: Pick<
    TableState<DeviceType>,
    'sortBy' | 'filters' | 'pageSize' | 'pageIndex'
  >,
  columns: Array<TableColumn>
) {
  return usePaginatedTableApiQuery<DeviceType>({
    baseUrl: DEVICES_API_URL,
    queryKey: [...devicesQueryKeys.list, tableState],
    tableState,
    columns,
    queryOptions: { staleTime: 0 },
  });
}

interface UseDeviceUsageBasedStateHistoriesParams {
  deviceId: string;
  from: Date;
  to: Date;
}
export function useDeviceUsageBasedStateHistories({
  deviceId,
  from,
  to,
}: UseDeviceUsageBasedStateHistoriesParams) {
  const buildUrl = () => {
    let url = `${DEVICES_API_URL}/${deviceId}/usage_based_state_histories`;
    const queryString = new URLSearchParams();
    queryString.append('from', from.toISOString());
    queryString.append('to', to.toISOString());

    url += `?${queryString.toString()}`;

    return url;
  };

  return useApiQuery<DeviceStateHistory[]>(
    buildUrl(),
    devicesQueryKeys.usageBasedStateHistories(deviceId),
    { staleTime: 0, enabled: !!deviceId }
  );
}

export function useUpdateDevice(deviceId: string) {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { url, options } = useRequestOptions({
    url: `${DEVICES_API_URL}/${deviceId}`,
    method: 'PUT',
  });

  return useMutation({
    mutationFn: (device: Partial<DeviceType>) =>
      fetchApiRequest(url, {
        ...options,
        body: JSON.stringify(device),
      }),
    onSuccess: () => {
      // TODO: Find a better way to optimistically update specific device data
      // Re-fetch devices data
      queryClient.invalidateQueries(devicesQueryKeys.list);

      dispatch(toastrSuccess('Note saved'));
    },
  });
}

interface UseBulkDeleteDevicesParams {
  device_ids: Array<string>;
}

export function useBulkDeleteDevices() {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { url, options } = useRequestOptions({
    url: '',
    method: 'POST',
  });

  return useMutation<void, { error: any }, UseBulkDeleteDevicesParams>({
    mutationFn: ({ device_ids: ids }) =>
      fetchApiRequest(`${url}/${DEVICES_API_URL}/bulk_destroy`, {
        ...options,
        body: JSON.stringify({ ids }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries(devicesQueryKeys.list);

      dispatch(toastrSuccess('Devices successfully removed'));
    },
    onError: ({ error }: { error: string }) => {
      dispatch(toastrError(error));
    },
  });
}

export function useAssignDevices() {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { url, options } = useRequestOptions({
    url: `${DEVICES_API_URL}/bulk_assign`,
    method: 'POST',
  });

  return useMutation({
    mutationFn: (assignmentData: {
      device_ids: Array<string>;
      organization_id: string;
    }) =>
      fetchApiRequest(url, {
        ...options,
        body: JSON.stringify(assignmentData),
      }),
    onSuccess: (data) => {
      // Re-fetch devices data
      queryClient.invalidateQueries(devicesQueryKeys.list);

      if (data.warning) {
        dispatch(toastrWarning(data.warning));
      }

      dispatch(toastrSuccess('Device successfully assigned'));
    },
    onError: ({ error }: { error: string }) => {
      dispatch(toastrError(error));
    },
  });
}
